import type {
  DiscountConditions,
  DiscountPeriod,
  DiscountRuleDto,
  FanTierDto,
  SkipReason,
} from '@shared/api/hooks/useDiscountRules';

const CATEGORY_IT: Record<string, string> = {
  JERSEY: 'maglie',
  SCARF: 'sciarpe',
  HAT: 'cappelli',
};

const PERIOD_IT: Record<DiscountPeriod, string> = {
  ALL_TIME: 'in totale',
  MONTH: 'al mese',
  SEASON: 'a stagione',
};

export const SKIP_REASON_IT: Record<SkipReason, string> = {
  TIER_NOT_ENTITLED: 'il tier del tifoso non e abilitato',
  LIMIT_TOTAL_REACHED: 'utilizzi totali esauriti',
  LIMIT_USER_REACHED: 'il tifoso ha esaurito i suoi utilizzi nel periodo',
  NOT_ACTIVE: 'regola disattivata',
  OUT_OF_WINDOW: 'fuori dalla finestra di validita',
  CODE_NOT_PROVIDED: 'serve il codice e non e stato inserito (o non corrisponde)',
  CONDITIONS_NOT_MET: 'le condizioni non sono soddisfatte',
  NO_MATCHING_ITEMS: 'nessun articolo del carrello corrisponde ai selettori',
  NOT_STACKABLE: 'non cumulabile con uno sconto gia applicato',
  STOPPED_BY_EXCLUSIVE: 'una regola esclusiva ha fermato la valutazione',
  ZERO_AMOUNT: 'lo sconto risultante sarebbe zero',
};

const eur = (v: string | number | null): string => {
  const n = typeof v === 'string' ? Number(v) : v;
  return n === null || Number.isNaN(n) ? '—' : `${n.toFixed(2)} EUR`;
};

/** "10%", "5.00 EUR", "spedizione gratuita", "2+1"… */
export function describeAction(rule: DiscountRuleDto): string {
  const v = rule.actionValue === null ? null : Number(rule.actionValue);
  switch (rule.actionType) {
    case 'PERCENTAGE': {
      const cap = rule.actionMaxDiscount ? `, max ${eur(rule.actionMaxDiscount)}` : '';
      return `-${v ?? 0}%${cap}`;
    }
    case 'FIXED_AMOUNT':
      return `-${eur(v)}`;
    case 'FIXED_PRICE':
      return `prezzo fisso ${eur(v)}`;
    case 'FREE_SHIPPING':
      return 'spedizione gratuita';
    case 'BXGY':
      return `${rule.bxgyBuyQty ?? '?'}+${rule.bxgyGetQty ?? '?'}`;
    default:
      return rule.actionType;
  }
}

function describeTarget(rule: DiscountRuleDto): string {
  if (rule.actionType === 'FREE_SHIPPING') return '';
  const c: DiscountConditions = rule.conditions ?? {};
  const parts: string[] = [];
  if (c.categories?.length) {
    parts.push(c.categories.map((k) => CATEGORY_IT[k] ?? k.toLowerCase()).join(', '));
  }
  if (c.skus?.length) parts.push(`SKU ${c.skus.join(', ')}`);
  if (c.productIds?.length) {
    parts.push(`${c.productIds.length} prodotti selezionati`);
  }
  if (parts.length === 0) return ' su tutto il carrello';
  const where = rule.targetScope === 'MATCHED_ITEMS' ? 'su' : 'sul carrello, se contiene';
  return ` ${where} ${parts.join(' / ')}`;
}

function describeConditions(c: DiscountConditions): string[] {
  const out: string[] = [];
  if (c.cartSubtotalMinCents) out.push(`carrello da almeno ${eur(c.cartSubtotalMinCents / 100)}`);
  if (c.cartQtyMin) out.push(`almeno ${c.cartQtyMin} articoli`);
  if (c.targetMinQty) out.push(`almeno ${c.targetMinQty} pezzi fra quelli selezionati`);
  if (c.firstPurchase) out.push('solo al primo acquisto');
  if (c.fulfillmentMethod === 'SHIPPING') out.push('solo con spedizione');
  if (c.fulfillmentMethod === 'IN_STORE_PICKUP') out.push('solo con ritiro allo stadio');
  return out;
}

function describeTiers(rule: DiscountRuleDto, tiers: FanTierDto[]): string {
  if (rule.tiers.length === 0) return 'per tutti i tifosi';
  const names = rule.tiers
    .map((t) => tiers.find((x) => x.id === t.tierId)?.key ?? '?')
    .join(', ');
  return `per i tifosi ${names}`;
}

function describeLimits(rule: DiscountRuleDto): string[] {
  const out: string[] = [];
  if (rule.maxRedemptionsPerUser) {
    out.push(`max ${rule.maxRedemptionsPerUser} volte a testa ${PERIOD_IT[rule.perUserPeriod]}`);
  }
  if (rule.maxRedemptionsTotal) out.push(`max ${rule.maxRedemptionsTotal} utilizzi complessivi`);
  if (!rule.stackable) out.push('non cumulabile');
  if (rule.exclusive) out.push('esclusiva: blocca le altre regole');
  return out;
}

function describeWindow(rule: DiscountRuleDto): string {
  const d = (s: string): string => new Date(s).toLocaleDateString('it-IT');
  if (rule.startsAt && rule.endsAt) return `dal ${d(rule.startsAt)} al ${d(rule.endsAt)}`;
  if (rule.startsAt) return `dal ${d(rule.startsAt)}`;
  if (rule.endsAt) return `fino al ${d(rule.endsAt)}`;
  return 'sempre attiva';
}

/**
 * Frase in italiano generata dalla configurazione della regola.
 *
 * Non e' decorazione: e' la difesa principale contro le regole scritte male.
 * Un admin che legge "-10% su maglie, per i tifosi ORO, max 2 volte a testa al
 * mese" si accorge dell'errore molto prima che leggendo otto campi separati.
 */
export function describeRule(rule: DiscountRuleDto, tiers: FanTierDto[]): string {
  const bits: string[] = [`${describeAction(rule)}${describeTarget(rule)}`];
  bits.push(describeTiers(rule, tiers));
  const conds = describeConditions(rule.conditions ?? {});
  if (conds.length) bits.push(conds.join(', '));
  bits.push(describeWindow(rule));
  const limits = describeLimits(rule);
  if (limits.length) bits.push(limits.join(', '));
  if (rule.trigger === 'CODE') bits.push(`si attiva col codice ${rule.code ?? '—'}`);
  return `${bits.join(' · ')}.`;
}
